import { useCallback, useEffect, useReducer, useState, type FormEvent } from 'react';
import {
  CheckCircleIcon,
  CircleNotchIcon,
  DeviceMobileIcon,
  IdentificationCardIcon,
} from '@phosphor-icons/react';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  formatWhatsAppPhoneOnBlur,
  isWhatsAppVerificationStillValid,
  normalizeWhatsAppPhoneInput,
  normalizeWhatsAppPhoneForSubmit,
} from '@/components/onboarding/platformOnboarding';
import { useOnboardingState } from '@/hooks/queries/useOnboardingState';
import { useProfile } from '@/hooks/queries/useProfile';
import { useAuthedFetcher } from '@/hooks/queries/swrConfig';
import { usersApi } from '@/lib/api';
import { formatCpf, normalizeCpf } from '@/lib/document';
import { track } from '@/lib/telemetry';
import { CONSENT_VERSION, STEP } from './flow.helpers';
import {
  initialValidationDraft,
  phoneSubmitError,
  requiredConsentGranted,
  validationBlockReason,
  validationReducer,
} from './validation.helpers';

export interface StepValidationProps {
  /** Called after every write landed — the flow persists the move to Conexões. */
  onContinue: () => void;
}

const BLOCK_HINTS = {
  cpf: 'Cadastre seu CPF para continuar.',
  whatsapp: 'Valide seu WhatsApp para continuar.',
  consent: 'Aceite os termos para continuar.',
} as const;

export function StepValidation({ onContinue }: StepValidationProps) {
  const authedFetch = useAuthedFetcher();
  const { data: profile, mutate: mutateProfile } = useProfile();
  const { data: serverState, mutate: mutateState } = useOnboardingState();
  const [draft, dispatch] = useReducer(validationReducer, initialValidationDraft);
  const [, setClock] = useState(0);

  // A reload mid-OTP loses nothing: the pending verification is SERVER state on
  // the profile, rehydrated through the same validity predicate the gate used.
  const pendingVerification = profile?.whatsAppPendingVerification;
  useEffect(() => {
    if (!isWhatsAppVerificationStillValid(pendingVerification)) return;
    if (draft.verification) return;
    dispatch({
      type: 'verification_started',
      verification: pendingVerification!,
      phone: pendingVerification!.maskedPhone,
    });
    // draft.verification is read, not depended on: hydration must run once per
    // fresh pending record, not re-fire on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingVerification]);

  useEffect(() => {
    if (!draft.verification) return;
    const interval = window.setInterval(() => setClock((value) => value + 1), 1000);
    return () => window.clearInterval(interval);
  }, [draft.verification]);

  const expiresSeconds = secondsUntil(draft.verification?.expiresAt);
  const resendSeconds = secondsUntil(draft.verification?.resendAvailableAt);
  useEffect(() => {
    if (!draft.verification || expiresSeconds > 0) return;
    dispatch({ type: 'verification_expired' });
  }, [draft.verification, expiresSeconds]);

  const consentGranted = requiredConsentGranted(serverState?.consents);
  const whatsAppRequired = profile?.whatsAppVerificationRequired === true;
  const whatsAppVerified = profile?.hasWhatsAppUserId === true;
  const busy = draft.pending !== 'idle';

  const blockReason = profile
    ? validationBlockReason(draft, profile, consentGranted)
    : 'cpf';

  const handleCpfChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const digits = normalizeCpf(event.target.value).slice(0, 11);
    dispatch({ type: 'cpf_changed', value: formatCpf(digits) ?? digits });
  }, []);

  const handlePhoneChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch({ type: 'phone_changed', value: normalizeWhatsAppPhoneInput(event.target.value) });
  }, []);

  const handlePhoneBlur = useCallback(() => {
    dispatch({ type: 'phone_blurred', value: formatWhatsAppPhoneOnBlur(draft.phone) });
  }, [draft.phone]);

  const handleCodeChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch({ type: 'code_changed', value: event.target.value });
  }, []);

  const handleRequiredConsent = useCallback((checked: boolean | 'indeterminate') => {
    dispatch({ type: 'required_consent_toggled', checked: checked === true });
  }, []);

  const startVerification = useCallback(async (event?: FormEvent) => {
    event?.preventDefault();
    if (busy) return;
    const invalid = phoneSubmitError(draft.phone);
    if (invalid) {
      dispatch({ type: 'failed', field: 'phone', message: invalid });
      return;
    }

    const normalized = normalizeWhatsAppPhoneForSubmit(draft.phone);
    dispatch({ type: 'pending', value: 'otp_start' });
    try {
      const verification = await authedFetch((token) =>
        usersApi.startWhatsAppVerification(token, normalized));
      track('action.onboarding.whatsapp_start');
      dispatch({
        type: 'verification_started',
        verification,
        phone: formatWhatsAppPhoneOnBlur(normalized),
      });
    } catch (err) {
      dispatch({
        type: 'failed',
        field: 'phone',
        message: err instanceof Error ? err.message : 'Não foi possível enviar o código.',
      });
    }
  }, [authedFetch, busy, draft.phone]);

  const resendVerification = useCallback(async () => {
    if (busy || !draft.verification || resendSeconds > 0) return;
    dispatch({ type: 'pending', value: 'otp_resend' });
    try {
      const normalized = normalizeWhatsAppPhoneForSubmit(draft.phone);
      const canRestart = normalized.length > 0 && !phoneSubmitError(draft.phone);
      const verification = await authedFetch((token) => canRestart
        ? usersApi.startWhatsAppVerification(token, normalized)
        : usersApi.resendWhatsAppVerification(token));
      track('action.onboarding.whatsapp_resend');
      dispatch({
        type: 'verification_started',
        verification,
        phone: canRestart ? formatWhatsAppPhoneOnBlur(normalized) : draft.phone,
      });
    } catch (err) {
      dispatch({
        type: 'failed',
        field: 'code',
        message: err instanceof Error ? err.message : 'Não foi possível enviar o código.',
      });
    }
  }, [authedFetch, busy, draft.phone, draft.verification, resendSeconds]);

  const confirmVerification = useCallback(async (event?: FormEvent) => {
    event?.preventDefault();
    if (busy) return;
    if (draft.code.length !== 6) {
      dispatch({ type: 'failed', field: 'code', message: 'Informe o código de 6 dígitos.' });
      return;
    }

    dispatch({ type: 'pending', value: 'otp_confirm' });
    try {
      const fresh = await authedFetch((token) =>
        usersApi.confirmWhatsAppVerification(token, draft.code));
      track('action.onboarding.whatsapp_confirm');
      await mutateProfile(fresh, { revalidate: false });
      dispatch({ type: 'verification_reset' });
    } catch (err) {
      dispatch({
        type: 'failed',
        field: 'code',
        message: err instanceof Error ? err.message : 'Não foi possível validar o código.',
      });
    }
  }, [authedFetch, busy, draft.code, mutateProfile]);

  const resetNumber = useCallback(() => {
    dispatch({ type: 'verification_reset' });
  }, []);

  // One serialized sequence, double-tap-proof via the pending guard: CPF write
  // (only when missing), then the required consent. Nothing else — the
  // WhatsApp-messages opt-in lives in Perfil (one tick, Davi 2026-08-26).
  const handleContinue = useCallback(async () => {
    if (busy || blockReason !== null || !profile) return;
    dispatch({ type: 'pending', value: 'continuing' });
    try {
      if (!profile.hasCpf) {
        const fresh = await authedFetch((token) =>
          usersApi.updateProfile(token, { cpf: normalizeCpf(draft.cpf) }));
        track('action.onboarding.submit_cpf');
        await mutateProfile(fresh, { revalidate: false });
      }

      // One tick only (Davi, 2026-08-26): the WhatsApp-messages opt-in lives in
      // Perfil, not here — LGPD needs it unbundled from the required acceptance,
      // and unbundled-but-adjacent read as two confusing ticks.
      if (!consentGranted) {
        const consents = await authedFetch((token) => usersApi.setConsent(
          token, 'terms_privacy_openfinance', { granted: true, version: CONSENT_VERSION }));
        track('action.onboarding.accept_consent', {
          purpose: 'terms_privacy_openfinance', accepted: true, version: CONSENT_VERSION,
        });
        await mutateState(
          (current) => (current ? { ...current, consents } : current),
          { revalidate: false },
        );
      }

      track('action.onboarding.step_completed', { step: STEP.validation, deferred: false });
      dispatch({ type: 'settled' });
      onContinue();
    } catch (err) {
      dispatch({
        type: 'failed',
        field: 'form',
        message: err instanceof Error ? err.message : 'Não foi possível salvar a validação.',
      });
    }
  }, [
    authedFetch, blockReason, busy, consentGranted, draft.cpf,
    mutateProfile, mutateState, onContinue, profile,
  ]);

  const errorFor = (field: string): string | null =>
    draft.error?.field === field ? draft.error.message : null;

  return (
    <section>
      <h1 className="text-[22px] font-bold tracking-tight">Vamos validar sua conta.</h1>
      <p className="mt-1 text-sm text-text-secondary">
        Precisamos do seu CPF e do seu WhatsApp antes de conectar seus bancos. Leva menos de um minuto.
      </p>

      <div className="mt-5 space-y-4">
        <ValidationBlock
          icon={<IdentificationCardIcon size={16} weight="duotone" />}
          satisfied={profile?.hasCpf === true}
          recap={`CPF cadastrado · ${profile?.cpfMasked ?? ''}`}
        >
          <Label htmlFor="validation-cpf" className="text-base/6 font-medium text-text-primary sm:text-sm/6">
            CPF
          </Label>
          <Input
            id="validation-cpf"
            name="cpf"
            type="text"
            inputMode="numeric"
            autoComplete="off"
            placeholder="000.000.000-00"
            value={draft.cpf}
            onChange={handleCpfChange}
            aria-invalid={!!errorFor('cpf')}
            aria-describedby={errorFor('cpf') ? 'validation-cpf-error' : 'validation-cpf-help'}
            className="h-12 rounded-xl text-base sm:h-10 sm:text-sm"
          />
          {errorFor('cpf') ? (
            <Alert id="validation-cpf-error" variant="destructive" className="rounded-xl text-sm">
              {errorFor('cpf')}
            </Alert>
          ) : (
            <p id="validation-cpf-help" className="text-base/7 text-text-tertiary sm:text-sm/6">
              Use o CPF do titular das contas que você vai conectar.
            </p>
          )}
        </ValidationBlock>

        <ValidationBlock
          icon={<DeviceMobileIcon size={16} weight="duotone" />}
          satisfied={whatsAppVerified}
          recap={`WhatsApp verificado · ${profile?.whatsAppMasked ?? ''}`}
        >
          <div className="flex items-baseline justify-between gap-2">
            <Label htmlFor="validation-whatsapp" className="text-base/6 font-medium text-text-primary sm:text-sm/6">
              WhatsApp
            </Label>
            {!whatsAppRequired && (
              <span className="text-2xs font-medium text-text-tertiary uppercase">opcional aqui</span>
            )}
          </div>
          <Input
            id="validation-whatsapp"
            name="whatsapp"
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            placeholder="11 99999-9999"
            value={draft.phone}
            onChange={handlePhoneChange}
            onBlur={handlePhoneBlur}
            readOnly={!!draft.verification}
            aria-invalid={!!errorFor('phone')}
            aria-describedby={errorFor('phone') ? 'validation-whatsapp-error' : 'validation-whatsapp-help'}
            className="h-12 rounded-xl text-base sm:h-10 sm:text-sm"
          />
          {errorFor('phone') ? (
            <Alert id="validation-whatsapp-error" variant="destructive" className="rounded-xl text-sm">
              {errorFor('phone')}
            </Alert>
          ) : (
            <p id="validation-whatsapp-help" className="text-base/7 text-text-tertiary sm:text-sm/6">
              Informe um celular com DDD. É por ele que a gente confirma que a conta é sua.
            </p>
          )}

          {!draft.verification ? (
            <Button
              type="button"
              variant="secondary"
              disabled={busy}
              onClick={startVerification}
              className="h-12 w-full text-base sm:h-10 sm:text-sm"
            >
              {draft.pending === 'otp_start' ? (
                <>
                  <CircleNotchIcon className="size-4 animate-spin" />
                  Enviando
                </>
              ) : (
                'Enviar código'
              )}
            </Button>
          ) : (
            <div aria-live="polite" className="space-y-3 rounded-xl border border-border-subtle bg-surface-sunken p-3">
              <p className="text-sm font-semibold text-text-primary">
                Enviamos um código para {draft.verification.maskedPhone}. Ele expira em poucos minutos.
              </p>
              <div className="space-y-2">
                <Label htmlFor="validation-whatsapp-code" className="text-base/6 font-medium text-text-primary sm:text-sm/6">
                  Código
                </Label>
                <Input
                  id="validation-whatsapp-code"
                  name="code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="000000"
                  value={draft.code}
                  onChange={handleCodeChange}
                  aria-invalid={!!errorFor('code')}
                  aria-describedby={errorFor('code') ? 'validation-whatsapp-code-error' : undefined}
                  className="h-12 rounded-xl text-base sm:h-10 sm:text-sm"
                />
              </div>
              {errorFor('code') && (
                <Alert id="validation-whatsapp-code-error" variant="destructive" className="rounded-xl text-sm">
                  {errorFor('code')}
                </Alert>
              )}
              <Button
                type="button"
                disabled={busy}
                onClick={confirmVerification}
                className="h-12 w-full text-base sm:h-10 sm:text-sm"
              >
                {draft.pending === 'otp_confirm' ? (
                  <>
                    <CircleNotchIcon className="size-4 animate-spin" />
                    Validando
                  </>
                ) : (
                  'Validar'
                )}
              </Button>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={resendVerification}
                  disabled={busy || resendSeconds > 0}
                >
                  {resendSeconds > 0 ? `Reenviar em ${resendSeconds}s` : 'Reenviar código'}
                </Button>
                <button
                  type="button"
                  onClick={resetNumber}
                  className="cursor-pointer text-sm/6 font-medium text-text-secondary hover:text-text-primary"
                >
                  Trocar número
                </button>
              </div>
            </div>
          )}
        </ValidationBlock>

        <div className="rounded-2xl border border-border-subtle bg-surface p-4">
          <ConsentCheckbox
            id="validation-consent-required"
            checked={consentGranted || draft.requiredConsent}
            disabled={consentGranted || busy}
            onCheckedChange={handleRequiredConsent}
          >
            Li e aceito os{' '}
            <a
              href="https://vertice.finance/termos"
              target="_blank"
              rel="noreferrer"
              className="text-brand-700 underline underline-offset-2"
            >
              Termos de Uso
            </a>{' '}
            e a{' '}
            <a
              href="https://vertice.finance/privacidade"
              target="_blank"
              rel="noreferrer"
              className="text-brand-700 underline underline-offset-2"
            >
              Política de Privacidade
            </a>
            . Você pode revogar quando quiser.
          </ConsentCheckbox>
        </div>
      </div>

      {errorFor('form') && (
        <Alert variant="destructive" className="mt-4 rounded-xl text-sm">
          {errorFor('form')}
        </Alert>
      )}

      <div className="mt-6 flex items-center justify-between gap-3 border-t border-border-subtle pt-4">
        <p aria-live="polite" className="min-w-0 text-xs/5 text-text-tertiary">
          {blockReason ? BLOCK_HINTS[blockReason] : ''}
        </p>
        <Button disabled={busy || blockReason !== null} onClick={handleContinue}>
          {draft.pending === 'continuing' && <CircleNotchIcon className="size-4 animate-spin" />}
          Continuar
        </Button>
      </div>
    </section>
  );
}

function ValidationBlock({
  icon,
  satisfied,
  recap,
  children,
}: {
  icon: React.ReactNode;
  satisfied: boolean;
  recap: string;
  children: React.ReactNode;
}) {
  if (satisfied) {
    return (
      <div className="flex items-center gap-3 rounded-2xl border border-border-subtle bg-surface p-4">
        <span className="flex size-8 shrink-0 items-center justify-center rounded-xl bg-brand-50 text-primary">
          <CheckCircleIcon size={16} weight="fill" />
        </span>
        <p className="min-w-0 flex-1 truncate text-sm font-medium">{recap}</p>
      </div>
    );
  }

  return (
    <div className="space-y-2 rounded-2xl border border-border-subtle bg-surface p-4">
      <span className="flex size-8 items-center justify-center rounded-xl bg-brand-50 text-primary">
        {icon}
      </span>
      {children}
    </div>
  );
}

function ConsentCheckbox({
  id,
  checked,
  disabled,
  onCheckedChange,
  children,
}: {
  id: string;
  checked: boolean;
  disabled: boolean;
  onCheckedChange: (checked: boolean | 'indeterminate') => void;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-3">
      <Checkbox
        id={id}
        checked={checked}
        disabled={disabled}
        onCheckedChange={onCheckedChange}
        className="mt-0.5"
      />
      <Label htmlFor={id} className="text-xs/5 font-normal text-pretty text-text-secondary">
        {children}
      </Label>
    </div>
  );
}

function secondsUntil(value?: string | null): number {
  if (!value) return 0;
  const target = new Date(value).getTime();
  if (!Number.isFinite(target)) return 0;
  return Math.max(0, Math.ceil((target - Date.now()) / 1000));
}
